import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  recordErrorStage,
  recordFunnelStep,
  recordLatency,
  recordPageView,
} from '../utils/rum';
import {
  buildOnboardingDestination,
  buildOnboardingSearch,
  finalizeOnboardingAfterAuth,
  resolveV2OnboardingState,
} from '../utils/v2Onboarding';
import { onboardingRumContext } from '../utils/v2Rum';

interface AuthCallbackProps {
  onAuthSuccess: (username: string, redirectTo?: string) => void;
}

function parseJwtPayload(token: string): Record<string, any> | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const normalized = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
}

const AuthCallback: React.FC<AuthCallbackProps> = ({ onAuthSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const onboardingState = resolveV2OnboardingState(location.search);
  const nextDestination = buildOnboardingDestination(onboardingState);
  const nextSearch = buildOnboardingSearch(onboardingState);

  useEffect(() => {
    const completeSignIn = async () => {
      const startedAt = performance.now();
      recordPageView('auth_callback_page', {
        route: '/auth/callback',
        ...onboardingRumContext(onboardingState),
      });
      recordFunnelStep('auth_callback', 'start');
      try {
        const session = await fetchAuthSession();

        if (!session.tokens?.idToken && !session.tokens?.accessToken) {
          throw new Error('No valid tokens found after OAuth redirect.');
        }

        const idTokenString = session.tokens?.idToken?.toString() || '';
        const claims = parseJwtPayload(idTokenString);
        recordFunnelStep('auth_callback', 'success', {
          redirect_target: nextDestination,
          ...onboardingRumContext(onboardingState),
        });
        recordLatency('auth_callback', performance.now() - startedAt);
        onAuthSuccess(claims?.email || claims?.['cognito:username'] || 'authenticated', nextDestination);
        finalizeOnboardingAfterAuth(onboardingState);
        navigate(nextDestination, { replace: true });
      } catch (callbackError: any) {
        setError(callbackError?.message || 'Unable to complete sign in.');
        recordFunnelStep('auth_callback', 'failure', {
          redirect_target: '/login',
        });
        recordErrorStage('auth_callback', callbackError);
        navigate(`/login${nextSearch}`, { replace: true });
      }
    };

    completeSignIn();
  }, [navigate, nextDestination, nextSearch, onAuthSuccess, onboardingState]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-200 p-6 md:p-8 text-center">
        <h1 className="text-2xl font-semibold text-slate-900 mb-2">Signing you in</h1>
        <p className="text-slate-600 mb-4">Finishing secure authentication...</p>
        {error ? <p className="text-red-700 text-sm">{error}</p> : null}
      </div>
    </div>
  );
};

export default AuthCallback;
